// ── auth.js ───────────────────────────────────────────────────────────────
// All authentication logic lives here.
// Passwords never appear in this file or anywhere in the frontend.
// JWT is stored in sessionStorage (cleared on tab close — by design).

const API_URL   = process.env.REACT_APP_API_URL;
const AUTH_KEY  = "ht_auth"; // stores { token, role }

// Typed error so the UI can distinguish auth failures from other errors
export class AuthError extends Error {
  constructor(msg) { super(msg); this.name = "AuthError"; }
}

// ── Storage ───────────────────────────────────────────────────────────────

export function getAuth() {
  try {
    const raw = sessionStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const auth = JSON.parse(raw);
    if (!auth?.token || !auth?.role) return null;
    return auth;
  } catch {
    return null;
  }
}

function storeAuth(token, role) {
  sessionStorage.setItem(AUTH_KEY, JSON.stringify({ token, role }));
}

export function logout() {
  sessionStorage.removeItem(AUTH_KEY);
}

// ── login(password) ───────────────────────────────────────────────────────
// Returns { ok: true, role } or { ok: false, error: string }

export async function login(password) {
  try {
    const resp = await fetch(`${API_URL}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });

    if (resp.status === 401) return { ok: false, error: "Incorrect password" };

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      return { ok: false, error: body.error || "Login failed" };
    }

    const data = await resp.json();
    storeAuth(data.token, data.role);
    return { ok: true, role: data.role };

  } catch {
    return { ok: false, error: "Network error" };
  }
}

// ── apiFetch(path, options) ───────────────────────────────────────────────
// Wraps fetch with Bearer auth. Throws AuthError on 401.

export async function apiFetch(path, options = {}) {
  const auth = getAuth();

  const headers = {
    "Content-Type": "application/json",
    ...(auth ? { "Authorization": `Bearer ${auth.token}` } : {}),
    ...(options.headers || {}),
  };

  const body = options.body && typeof options.body === "object"
    ? JSON.stringify(options.body)
    : options.body;

  const resp = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    ...(body ? { body } : {}),
  });

  if (resp.status === 401) {
    logout();
    throw new AuthError("Session expired — please log in again");
  }

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: resp.statusText }));
    throw new Error(err.error || "API error");
  }

  return resp.json();
}
